import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, query, orderBy, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { useArtists } from '../hooks/useFirestore';
import { Modal } from './Tasks';
import { Plus, ChevronDown, ChevronUp, Trash2, ExternalLink } from 'lucide-react';

// ── PROTOCOL TEMPLATE from WeMojave doc ─────────────────────
const RELEASE_TEMPLATE = [
  // Distribution
  { id: 't1',  label: 'Upload to DistroKid',                              category: 'DIST',    required: true  },
  { id: 't2',  label: 'Pitch in Spotify for Artists (min 1 week, aim 4)', category: 'SPOTIFY', required: true, note: 'Mandatory for Release Radar + editorial consideration' },
  // Publishing
  { id: 't3',  label: 'Register song with BMI / ASCAP',                   category: 'PUBLISH', required: true  },
  { id: 't4',  label: 'Add all songwriters + splits',                      category: 'PUBLISH', required: true  },
  { id: 't5',  label: 'Register publisher (if self-published)',            category: 'PUBLISH', required: false },
  // MLC
  { id: 't6',  label: 'Register at TheMLC.com',                           category: 'MLC',     required: true, note: 'Collects U.S. streaming mechanical royalties — most indie artists forget this' },
  { id: 't7',  label: 'Confirm songwriter + publisher info at MLC',        category: 'MLC',     required: true  },
  { id: 't8',  label: 'Match ISRC + metadata at MLC',                     category: 'MLC',     required: true  },
  // SoundExchange
  { id: 't9',  label: 'Register as featured artist on SoundExchange',     category: 'SE',      required: true, note: 'Collects SiriusXM / Pandora non-interactive royalties' },
  { id: 't10', label: 'Register sound recording on SoundExchange',        category: 'SE',      required: true  },
  { id: 't11', label: 'Confirm ISRC matches distributor',                  category: 'SE',      required: true  },
  // Spotify
  { id: 't12', label: 'Upload Spotify Canvas (vertical 9:16, under 8s loop)', category: 'SPOTIFY', required: true },
  { id: 't13', label: 'Add to personal artist playlist',                  category: 'SPOTIFY', required: true  },
  { id: 't14', label: 'Update Spotify bio',                               category: 'SPOTIFY', required: false },
  // Metadata
  { id: 't15', label: 'Update Komi',                                      category: 'META',    required: false },
  { id: 't16', label: 'Submit lyrics to Musixmatch',                      category: 'META',    required: false },
  { id: 't17', label: 'Add to website',                                   category: 'META',    required: false },
  // Week 1 follow-up
  { id: 't18', label: 'Register with Songtrust (if using)',               category: 'WEEK1',   required: false, note: 'Collects global mechanical + performance' },
  { id: 't19', label: 'Confirm PRO registration status "active"',         category: 'WEEK1',   required: true  },
  { id: 't20', label: 'Confirm SoundExchange recording approved',         category: 'WEEK1',   required: true  },
  { id: 't21', label: 'Screenshot first-week data',                       category: 'WEEK1',   required: true  },
];

const CATEGORY_META = {
  DIST:    { label: 'Distribution',   color: '#e8621a' },
  SPOTIFY: { label: 'Spotify',        color: '#1db954' },
  PUBLISH: { label: 'Publishing',     color: '#7b4fc4' },
  MLC:     { label: 'The MLC',        color: '#c98a18' },
  SE:      { label: 'SoundExchange',  color: '#1a9080' },
  META:    { label: 'Metadata',       color: '#4a9eed' },
  WEEK1:   { label: 'Week 1 Follow-up', color: '#a87ee8' },
};


function useReleases(artistId) {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const q = query(collection(db, 'releases'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      let all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (artistId) all = all.filter(r => r.artistId === artistId);
      setReleases(all);
      setLoading(false);
    });
  }, [artistId]);
  const addRelease = async (r) => addDoc(collection(db, 'releases'), { ...r, createdAt: serverTimestamp() });
  const updateRelease = async (id, updates) => updateDoc(doc(db, 'releases', id), updates);
  const deleteRelease = async (id) => deleteDoc(doc(db, 'releases', id));
  return { releases, loading, addRelease, updateRelease, deleteRelease };
}

export default function Releases({ activeArtistId }) {
  const { profile, isManager } = useAuth();
  const { artists } = useArtists();
  const { releases, loading, addRelease, updateRelease, deleteRelease } = useReleases(activeArtistId);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showProtocol, setShowProtocol] = useState(false);
  const [form, setForm] = useState({ songTitle: '', artistId: activeArtistId || '', targetDate: '', spotifyPitchDate: '' });

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const toggleItem = async (release, itemId) => {
    const items = (release.checklistItems || []).map(i =>
      i.id === itemId ? { ...i, done: !i.done } : i
    );
    await updateRelease(release.id, { checklistItems: items });
  };

  const progress = (release) => {
    const items = (release.checklistItems || []);
    if (!items.length) return 0;
    return Math.round((items.filter(i => i.done).length / items.length) * 100);
  };

  const handleAdd = async () => {
    if (!form.songTitle.trim()) return;
    const checklistItems = RELEASE_TEMPLATE.map(t => ({ ...t, done: false }));
    await addRelease({ ...form, checklistItems, artistId: form.artistId || (isManager ? '' : profile?.artistId) });
    setForm({ songTitle: '', artistId: activeArtistId || '', targetDate: '', spotifyPitchDate: '' });
    setShowAdd(false);
  };

  const artistName = (id) => artists.find(a => a.id === id)?.name || '';
  const artistColor = (id) => artists.find(a => a.id === id)?.color || 'var(--purple)';

  // Group checklist items by category
  const groupByCategory = (items) => {
    const groups = {};
    items.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--muted)' }}>Loading...</div>;

  return (
    <div style={{ padding: '28px 30px', position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>🚀 Release Checklist</h1>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
            {releases.length} release{releases.length !== 1 ? 's' : ''} · Full WeMojave protocol
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>

          <button onClick={() => setShowProtocol(true)} className="btn btn-ghost btn-sm">📋 Protocol Guide</button>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary"><Plus size={15}/> New Release</button>
        </div>
      </div>

      {/* Releases */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {releases.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
            No releases yet — start a new one to load the full protocol
          </div>
        )}
        {releases.map(release => {
          const pct = progress(release);
          const artist = artists.find(a => a.id === release.artistId);
          const isExpanded = expanded[release.id];
          const groups = groupByCategory(release.checklistItems || []);
          const doneCount = (release.checklistItems || []).filter(i => i.done).length;
          const totalCount = (release.checklistItems || []).length;
          const isOverdue = release.spotifyPitchDate && new Date(release.spotifyPitchDate) < new Date();

          return (
            <div key={release.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              {/* Release header */}
              <div
                style={{ padding: '18px 22px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
                onClick={() => toggleExpand(release.id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>{release.songTitle}</div>
                    {artist && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600, background: `${artistColor(release.artistId)}22`, color: artistColor(release.artistId), fontFamily: 'var(--mono)' }}>{artist.name}</span>}
                    {pct === 100 && <span style={{ fontSize: 10, color: '#27ae60', fontFamily: 'var(--mono)', fontWeight: 700 }}>✓ Complete</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {release.targetDate && <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Target: {release.targetDate}</span>}
                    {release.spotifyPitchDate && <span style={{ fontSize: 11, color: isOverdue ? '#e74c3c' : 'var(--teal2)', fontFamily: 'var(--mono)' }}>Spotify pitch: {release.spotifyPitchDate}{isOverdue ? ' ⚠ overdue' : ''}</span>}
                    <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{doneCount}/{totalCount} complete</span>
                  </div>
                </div>
                {/* Progress */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ width: 100, height: 6, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? 'var(--teal2)' : 'linear-gradient(90deg,var(--purple),var(--orange))', borderRadius: 3, transition: 'width .4s' }}/>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: pct === 100 ? 'var(--teal2)' : 'var(--orange2)', fontFamily: 'var(--mono)', width: 36 }}>{pct}%</div>
                  {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--dim)' }}/> : <ChevronDown size={16} style={{ color: 'var(--dim)' }}/>}
                </div>
              </div>

              {/* Checklist */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '16px 22px' }}>
                  {Object.entries(groups).map(([cat, items]) => {
                    const catMeta = CATEGORY_META[cat] || { label: cat, color: 'var(--muted)' };
                    const catDone = items.filter(i => i.done).length;
                    return (
                      <div key={cat} style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: catMeta.color, flexShrink: 0 }}/>
                          <div style={{ fontSize: 10, fontWeight: 700, color: catMeta.color, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'var(--mono)', flex: 1 }}>{catMeta.label}</div>
                          <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{catDone}/{items.length}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {items.map(item => (
                            <div
                              key={item.id}
                              onClick={() => toggleItem(release, item.id)}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 7, background: item.done ? 'rgba(39,174,96,.06)' : 'var(--surface2)', border: `1px solid ${item.done ? 'rgba(39,174,96,.2)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}
                            >
                              <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${item.done ? 'var(--teal2)' : 'var(--dim)'}`, background: item.done ? 'var(--teal2)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all .15s' }}>
                                {item.done && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 500, color: item.done ? 'var(--dim)' : 'var(--text)', textDecoration: item.done ? 'line-through' : 'none' }}>
                                  {item.label}
                                  {item.required && !item.done && <span style={{ marginLeft: 6, fontSize: 9, color: '#e74c3c', fontFamily: 'var(--mono)' }}>required</span>}
                                </div>
                                {item.note && <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 2, lineHeight: 1.5 }}>{item.note}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Delete */}
                  {isManager && (
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => deleteRelease(release.id)} className="btn btn-sm" style={{ background: 'rgba(192,57,43,.1)', color: '#e74c3c', border: '1px solid rgba(192,57,43,.3)' }}>
                        <Trash2 size={12}/> Delete Release
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Release Modal */}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="New Release">
          <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, background: 'rgba(39,174,96,.08)', border: '1px solid rgba(39,174,96,.2)', fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
            This will create a release with the full WeMojave protocol pre-loaded (21 checklist items across Distribution, Spotify, Publishing, MLC, SoundExchange, Metadata, and Week 1 Follow-up).
          </div>
          <div className="form-group"><label>Song Title</label><input autoFocus value={form.songTitle} onChange={e => setForm(f => ({ ...f, songTitle: e.target.value }))} placeholder="e.g. Astronaut Today Pt. II"/></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {isManager && (
              <div className="form-group">
                <label>Artist</label>
                <select value={form.artistId} onChange={e => setForm(f => ({ ...f, artistId: e.target.value }))}>
                  <option value="">Select...</option>
                  {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group"><label>Target Release Date</label><input type="date" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))}/></div>
            <div className="form-group">
              <label>Spotify Pitch Date</label>
              <input type="date" value={form.spotifyPitchDate} onChange={e => setForm(f => ({ ...f, spotifyPitchDate: e.target.value }))}/>
              <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 4 }}>Aim 4 weeks before release</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={handleAdd} disabled={!form.songTitle.trim()} className="btn btn-primary">🚀 Start Release</button>
          </div>
        </Modal>
      )}

      {/* Protocol Guide Modal */}
      {showProtocol && (
        <Modal onClose={() => setShowProtocol(false)} title="📋 Protocol Quick Ref">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { title: '🎧 Master', color: '#e8621a', items: ['DistroKid → streaming', 'SoundExchange → SiriusXM/Pandora', 'YouTube Content ID (via distro)'] },
              { title: '✍️ Publishing', color: '#7b4fc4', items: ['BMI/ASCAP → performance royalties', 'The MLC → U.S. mechanicals', 'Songtrust → global (optional)'] },
              { title: '🎵 Spotify', color: '#1db954', items: ['Pitch min 1 wk, aim 4 wks out', 'Canvas: 9:16 vertical, <8s loop', 'Add to artist playlist'] },
              { title: '📅 Week 1', color: '#a87ee8', items: ['Screenshot first-week data', 'Confirm PRO registration active', 'Confirm SoundExchange approved'] },
            ].map(section => (
              <div key={section.title} style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--surface2)', border: `1px solid ${section.color}33` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: section.color, marginBottom: 8 }}>{section.title}</div>
                {section.items.map((item, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, display: 'flex', gap: 6 }}>
                    <span style={{ color: section.color, flexShrink: 0 }}>·</span>{item}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 8, background: 'rgba(232,98,26,.08)', border: '1px solid rgba(232,98,26,.2)', fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--orange2)' }}>Master vs Publishing:</strong> Every song has two copyrights. Master = the audio file (DistroKid + SoundExchange). Publishing = the songwriting (BMI/ASCAP + The MLC). Both must be registered separately.
          </div>
        </Modal>
      )}
    </div>
  );
}
